import { getAllQuestionnaireAnswers } from "@medplum/core";
import {
  CodeableConcept,
  Coding,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";

import { getQuestionnaireAnswerValue } from "@/utils/questionnaires";

import {
  BILLING_PAYMENT_METHOD_LABEL_INSURANCE,
  BILLING_PAYMENT_METHOD_LABEL_SELF_PAY,
  BillingPaymentMethodKind,
} from "../constants/billing-payment-method";
import type { ResolvedBillingPaymentMethod } from "../types/billing-payment-method";

const PAYMENT_METHOD_LINK_ID = "billingInformationPaymentMethod";

const SELF_PAY_CODE = "intake/billing-information/payment-method/self-pay";

/**
 * Extracts the payment-method coding code from an intake QuestionnaireResponse answer item.
 */
function getPaymentMethodCodeFromAnswer(
  answer: QuestionnaireResponseItemAnswer | undefined,
): string | undefined {
  if (!answer) return undefined;

  const withCodeable = answer as QuestionnaireResponseItemAnswer & {
    valueCodeableConcept?: CodeableConcept;
  };
  const fromCodeable = withCodeable.valueCodeableConcept?.coding?.[0]?.code?.trim();
  if (fromCodeable) return fromCodeable;

  if (answer.valueCoding?.code?.trim()) return answer.valueCoding.code.trim();

  const scalar = getQuestionnaireAnswerValue([answer]);
  if (typeof scalar === "object" && scalar !== null && "code" in scalar) {
    const coding = scalar as Coding;
    return coding.code?.trim() || undefined;
  }

  return undefined;
}

/**
 * Maps an intake payment-method code to the canonical billing payer kind used for fee resolution.
 *
 * Self-pay maps to self-pay; any other non-empty payment-method code (private insurance, Medicaid,
 * government, etc.) maps to insurance.
 */
export function mapPaymentMethodCodeToBillingKind(
  code: string | undefined,
): ResolvedBillingPaymentMethod["kind"] {
  const trimmed = code?.trim();
  if (!trimmed) return "undetermined";
  if (trimmed === SELF_PAY_CODE || trimmed.endsWith("/self-pay")) {
    return BillingPaymentMethodKind.SelfPay;
  }
  return BillingPaymentMethodKind.Insurance;
}

/**
 * Resolves Intake “how do you expect to pay” into insurance vs self-pay for billing.
 *
 * @param questionnaireResponse Latest client-intake QuestionnaireResponse, if any
 * @returns Canonical billing payment method, or undetermined when missing/unparseable
 */
export function resolveBillingPaymentMethodFromIntake(
  questionnaireResponse: QuestionnaireResponse | undefined,
): ResolvedBillingPaymentMethod {
  if (!questionnaireResponse) return { kind: "undetermined" };

  const answers = getAllQuestionnaireAnswers(questionnaireResponse);
  const items = answers[PAYMENT_METHOD_LINK_ID] ?? [];
  const code = getPaymentMethodCodeFromAnswer(items[0]);
  const kind = mapPaymentMethodCodeToBillingKind(code);

  if (kind === "undetermined" || !code) {
    return { kind: "undetermined" };
  }

  return {
    kind,
    code,
    displayLabel:
      kind === BillingPaymentMethodKind.SelfPay
        ? BILLING_PAYMENT_METHOD_LABEL_SELF_PAY
        : BILLING_PAYMENT_METHOD_LABEL_INSURANCE,
  };
}
